// Kwik Signs pricing — corflute & foamboard.
// Prices are AUD, per board, excluding GST. Double-sided = unit × 1.5.
// Custom cut is at no additional cost.

export const SIGN_SIZES = ["A0", "A1", "A2", "A3", "A4"];
export const SIGN_QTYS = [1, 3, 5, 10, 20, 50, 100];
export const SIGN_SIDES = [
  { id: "single", label: "Single-sided" },
  { id: "double", label: "Double-sided" },
];
export const SIGN_CUTS = [
  { id: "standard", label: "Standard rectangular" },
  { id: "custom", label: "Custom cut to shape" },
];

export const CORFLUTE_PRICING = {
  1:   { A0: 99,  A1: 59, A2: 39, A3: 29, A4: 19 },
  3:   { A0: 89,  A1: 49, A2: 32, A3: 24, A4: 16 },
  5:   { A0: 79,  A1: 42, A2: 28, A3: 20, A4: 13 },
  10:  { A0: 69,  A1: 34, A2: 24, A3: 17, A4: 11 },
  20:  { A0: 59,  A1: 29, A2: 20, A3: 14, A4: 9 },
  50:  { A0: 49,  A1: 24, A2: 16, A3: 11, A4: 7.5 },
  100: { A0: 42,  A1: 20, A2: 13, A3: 9,  A4: 6.5 },
};

export const FOAMBOARD_PRICING = {
  1:   { A0: 149, A1: 89, A2: 59, A3: 39, A4: 25 },
  3:   { A0: 139, A1: 79, A2: 52, A3: 35, A4: 22 },
  5:   { A0: 129, A1: 69, A2: 47, A3: 31, A4: 19 },
  10:  { A0: 119, A1: 59, A2: 42, A3: 27, A4: 17 },
  20:  { A0: 109, A1: 52, A2: 36, A3: 23, A4: 14 },
  50:  { A0: 99,  A1: 47, A2: 31, A3: 20, A4: 12 },
  100: { A0: 89,  A1: 42, A2: 27, A3: 17, A4: 10 },
};

// 3mm PVC Signs — AUD per board, ex GST. Double-sided = unit × 1.5.
export const PVC_PRICING = {
  1:   { A0: 119, A1: 69, A2: 45, A3: 32, A4: 22 },
  3:   { A0: 109, A1: 59, A2: 39, A3: 27, A4: 19 },
  5:   { A0: 99,  A1: 52, A2: 35, A3: 24, A4: 16 },
  10:  { A0: 89,  A1: 44, A2: 29, A3: 21, A4: 14 },
  20:  { A0: 79,  A1: 39, A2: 26, A3: 18, A4: 12 },
  50:  { A0: 69,  A1: 32, A2: 22, A3: 15, A4: 10 },
  100: { A0: 59,  A1: 28, A2: 18, A3: 12, A4: 8 },
};

const TABLE = { corflute: CORFLUTE_PRICING, foamboard: FOAMBOARD_PRICING, pvc: PVC_PRICING };

export function signUnitPrice(product, size, qty, sides = "single") {
  const base = TABLE[product]?.[qty]?.[size];
  if (base == null) return null;
  return sides === "double" ? base * 1.5 : base;
}

// Eyelets add-on (corflute only). $1 per eyelet, ex GST.
export const EYELET_PRICE = 1;
export const EYELET_OPTIONS = [0, 2, 4, 6, 8];

export function signLineTotal(product, size, qty, sides = "single", eyelets = 0) {
  const unit = signUnitPrice(product, size, qty, sides);
  if (unit == null) return null;
  const boardsTotal = unit * qty;
  const eyeletsTotal = product === "corflute" ? (eyelets || 0) * EYELET_PRICE * qty : 0;
  return boardsTotal + eyeletsTotal;
}

// Format currency with cents when needed (e.g. $7.50) but no trailing .00 for whole dollars
const FMT = new Intl.NumberFormat("en-AU", {
  style: "currency", currency: "AUD",
  minimumFractionDigits: 0, maximumFractionDigits: 2,
});
export function formatAUDexact(n) {
  if (n == null) return "—";
  // force 2 decimals if cents are non-zero
  const hasCents = Math.round(n * 100) % 100 !== 0;
  return new Intl.NumberFormat("en-AU", {
    style: "currency", currency: "AUD",
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(n);
}

export const SIGN_PRODUCTS = {
  corflute: {
    id: "corflute",
    name: "5mm Corflute Signs",
    shortName: "Corflute",
    headline: "5mm Corflute Signs",
    tagline: "Full-colour custom corflute signs, professionally printed on 5 mm corflute board.",
    image: "/products/corflute-sign.webp",
    imageAlt: "Corflute sign example",
    imageTag: "Corflute example",
    boardLabel: "5 mm corflute board",
    uses: ["Real estate", "Construction", "Events", "Promotions", "Retail", "Site signage", "Temporary outdoor"],
  },
  foamboard: {
    id: "foamboard",
    name: "5mm Foamboard Printing",
    shortName: "Foamboard",
    headline: "5mm Foamboard Printing",
    tagline: "Full-colour custom foamboard printing on 5 mm foamboard, ideal for indoor displays, presentations and promotional applications.",
    image: "/products/foamboard-sign.webp",
    imageAlt: "Foamboard sign example",
    imageTag: "Foamboard example",
    boardLabel: "5 mm foamboard",
    uses: ["Presentation boards", "Retail displays", "Exhibitions", "Events", "Point-of-sale", "Promotional boards", "Photography"],
  },
  pvc: {
    id: "pvc",
    name: "3mm PVC Signs",
    shortName: "PVC",
    headline: "3mm PVC Signs",
    tagline: "Full-colour custom signs printed on durable 3 mm white PVC board — rigid, water-resistant and ideal for long-term indoor or sheltered outdoor use.",
    image: "/products/pvc-example.webp",
    imageAlt: "3mm PVC sign example",
    imageTag: "PVC example",
    boardLabel: "3 mm white PVC board",
    uses: ["Wayfinding", "Shop fronts", "Office signage", "Trade & reception", "Exhibitions", "Durable outdoor", "Point-of-sale"],
  },
  pullup: {
    id: "pullup",
    name: "Pull-Up Banner",
    shortName: "Pull-Up Banner",
    headline: "Pull-Up Banners",
    tagline: "Full-colour 850 × 2000 mm pull-up banners — complete with retractable stand and carry bag. Set up in seconds.",
    image: "/products/pull-up-banner.webp",
    imageAlt: "Pull-up banner example",
    imageTag: "Pull-up banner example",
    uses: ["Trade shows", "Expos", "Events", "Retail", "Reception areas", "Conferences", "Promotions"],
    features: ["850 × 2000 mm", "Full-colour, single-sided", "Retractable stand + carry bag included", "Flat $150 per banner", "Printed locally in Australia"],
  },
  coaster: {
    id: "coaster",
    name: "Custom Printed Coasters",
    shortName: "Coasters",
    headline: "Custom Printed Coasters",
    tagline: "Custom full-colour printed drink coasters for cafes, restaurants, breweries, events, weddings, product packaging and promotional merchandise.",
    image: "/products/coasters.webp",
    imageAlt: "Custom printed drink coasters",
    imageTag: "Printed coaster example",
    uses: ["Cafes", "Restaurants", "Breweries", "Events", "Weddings", "Product packaging", "Promotional merch"],
    features: ["95mm round or 95mm square with rounded corners", "Full-colour CMYK printing", "Absorbent coaster / beermat board", "Single or double-sided", "Professional printed finish", "Minimum order 500"],
  },
};

// Coasters — fixed total order prices, AUD ex GST.
export const COASTER_QTYS = [500, 1000, 2000, 3000, 5000, 10000];
export const COASTER_PRICING = {
  single: { 500: 225, 1000: 300, 2000: 500, 3000: 600, 5000: 850, 10000: 1500 },
  double: { 500: 325, 1000: 400, 2000: 600, 3000: 750, 5000: 950, 10000: 1700 },
};
export const COASTER_SIZE = "95 mm";
export const COASTER_SHAPES = [
  { id: "round", label: "95mm Round", note: "Circular · 95 mm diameter" },
  { id: "square", label: "95mm Square", note: "95 × 95 mm · rounded corners" },
];
export const coasterPrice = (qty, sides) => COASTER_PRICING[sides][qty];

// Pull-up banners — flat AUD per banner, ex GST.
export const PULLUP_PRICE = 150;
export const PULLUP_SIZE = "850 × 2000 mm";
export const PULLUP_QTYS = [1, 2, 3, 4, 5];
